'use client';

import { useDispatch, useSelector } from 'react-redux';
import { lazy, useEffect, useMemo, useState } from 'react';
import { Skeleton } from '@heroui/skeleton';
import { useUserLocale } from '@/hooks/useUserLocale';

import { getCurrentBearerToken, redirectToLogin } from '@/lib/client-auth';
import { isBlank } from '@/lib/utils';
import { Spinner } from '@heroui/spinner';
import { UserLangProgress } from '@/components/Types';
import { RootState } from '@/store/store';
import { loadCurrentUser, loadCurrentUserLangProgress } from '@/store/actions/app';
import { hasFeedCourse } from '@/lib/constants';
import { useRouter } from 'next/navigation';
import AnimatedLogoText from '@/components/AnimatedLogoText';
import Image from 'next/image';
import { usersDataTools } from '@/lib/data/users-data-tools';

const LandingPageLazy = lazy(() => import('./login/LandingPage'));
const LearningFeedPageLazy = lazy(() => import('./learning-feed/LearningFeedPage'));
const OldDashboardPageLazy = lazy(() => import('./dashboard/OldDashboardPage'));

export default function HomePage() {
  const { t, locale } = useUserLocale();
  const isFlutterWebview = useSelector((state: any) => state.app.isFlutterWebview);
  const user = useSelector((state: any) => state.app.currentUser);
  const languageProgress = useSelector((state: RootState) => state.app.languageProgress) as UserLangProgress | null;
  const enableNewUi = useSelector((state: any) => state.app.featureFlags?.enableNewUi);
  const [resolved, setResolved] = useState(false);

  const hasAuthToken = useMemo(() => {
    return !isBlank(getCurrentBearerToken());
  }, [user?._id]);

  const router = useRouter();
  const dispatch = useDispatch();

  useEffect(() => {
    if (user == null) {
      if (isBlank(getCurrentBearerToken())) {
        // Do nothing, just show the guest landing page
        setResolved(true);
      } else {
        loadCurrentUser(dispatch).then(user => {
          setResolved(true);
        }).catch((e) => {
          console.error('Error loading user:', e);
          setResolved(true);
          // redirectToLogin(router);
        });
      }      
    } else {
      setResolved(true);
    }
  }, [user?._id]);

  // Load initial data
  useEffect(() => {
    if (user != null && user.learningLanguage != null && languageProgress?._id == null && !usersDataTools.shouldUserOnboardNow(user)) {
      loadCurrentUserLangProgress(dispatch, user?.learningLanguage as string);    
    } else if (user && usersDataTools.shouldUserOnboardNow(user)) {
      router.push('/onboard');
    } else if (user && user.learningLanguage == null) {
      console.error('No language selected');
    } else if (user && (user.onboarded || usersDataTools.isGuestUser(user)) && hasFeedCourse(user.learningLanguage, user.motherLanguage)) {
      // // Redirect to new dashboard for feed-complete courses
      router.push('/learning-feed');
    } else if (user && (user.onboarded || usersDataTools.isGuestUser(user))) {
      // // Redirect to new dashboard for feed-complete courses
      router.push('/dashboard');
    } 
  }, [user?._id, languageProgress?._id, resolved, hasAuthToken]);

  if (resolved && !hasAuthToken) {
    return <LandingPageLazy />;
  } else {
    return <div className="h-screen w-full max-w-3xl mx-auto px-2 sm:px-4 flex flex-col gap-4 justify-center items-center">
      <div className="flex flex-col items-center justify-center mb-6 mt-4">
        <Image src="/images/logo-web-lush.png" alt="Language Tutor" width={120} height={120} className="w-24 h-24 sm:w-32 sm:h-32" priority={true} />
        <AnimatedLogoText displaySubtext={false} />
      </div>

        <h1 className="mb-1 text-gray-600 text-center w-4/5 md:w-3/4 mx-auto text-lg">{t.landing.demoChat.tagline}</h1>
      <div className="w-full md:w-10/12 max-w-3xl">
      </div>
      
      <Spinner size="lg" color="secondary" />
    </div>;
  }
}